import React, { createContext, PropsWithChildren, useContext } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { LicenseInfo } from "@mui/x-license-pro";
import type Config from "../public/assets/appSettings.json";
import FallbackConfig from "./Resources/fallbackSettings.json";
import merge from "deepmerge";
import { ApiConfiguration } from "./Hooks/config";

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENCE ?? "");

fetch("/assets/appSettings.json")
    .then((response) => response.json())
    .then((config) => {
        console.log("Loaded configuration:", FallbackConfig);
        const merged = merge(FallbackConfig, config) as typeof Config;
        const mergedExpanded: PartialAppConfig = {
            ...merged,
            apiConfig: {
                tradesApiEndpoint: merged.tradesApiEndpoint,
                refDataApiEndpoint: merged.refDataApiEndpoint,
                apiKey: merged.apiKey,
                refDataApiKey: merged.refDataApiKey,
            },
        };
        const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
        root.render(
            <React.StrictMode>
                <AppConfigProvider config={mergedExpanded}>
                    <App />
                </AppConfigProvider>
            </React.StrictMode>
        );
    })
    .catch((error) => {
        const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
        root.render(<h1>Failed to load configuration</h1>);
        console.error("Error loading configuration:", error);
    });

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

type PartialAppConfig = Partial<typeof Config> & {
    apiConfig: ApiConfiguration;
};

const appConfigContext = createContext<PartialAppConfig | undefined>(undefined);

export const useGlobalAppConfig = (): PartialAppConfig => {
    const context = useContext(appConfigContext);

    if (!context) {
        throw new Error("useGlobalAppConfig must be used within an AppConfigProvider");
    }

    return context;
};

type Props = {
    config: PartialAppConfig;
};

const AppConfigProvider = ({ children, config }: PropsWithChildren<Props>) => {
    return <appConfigContext.Provider value={config}>{children}</appConfigContext.Provider>;
};
